import { lazy, Suspense, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import KakaoFloatingButton from "@/components/KakaoFloatingButton";

const ChatbotWidget = lazy(() => import("@/components/ChatbotWidget"));
const MacbookScrollOpenSection = lazy(
  () => import("@/components/sections/MacbookScrollOpenSection"),
);
import MainHeroSection from "@/components/sections/MainHeroSection";
import HeroSection from "@/components/sections/HeroSection";
import AboutSection from "@/components/sections/AboutSection";
import AutomationSection from "@/components/sections/AutomationSection";
import AIPlatformSection from "@/components/sections/AIPlatformSection";
import ContactSection from "@/components/sections/ContactSection";
import { resetHomeScrollOnReload } from "@/lib/scrollReset";

/** Legacy Spline Hero preview for device iframes. Not the current homepage CMS. */
function LandingEmbedPage() {
  return (
    <main
      data-testid="landing-embed-page"
      className="ds-landing ds-landing-embed min-h-0 bg-[#050505] text-white relative w-full overflow-x-clip"
    >
      <Header />
      <HeroSection showDevicePreview={false} embedMode />
      <AboutSection />
    </main>
  );
}

export function CurrentHomePage({ chrome = true }) {
  return (
    <>
      {chrome ? <Header /> : null}
      <MainHeroSection />
      <Suspense fallback={null}>
        <MacbookScrollOpenSection />
      </Suspense>
      <AboutSection />
      <AutomationSection />
      <AIPlatformSection />
      <ContactSection />
      {chrome ? <Footer /> : null}
    </>
  );
}

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    if (resetHomeScrollOnReload()) return undefined;

    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return undefined;
    }

    const id = location.hash.slice(1);
    let cancelled = false;
    let attempts = 0;

    const scrollToHash = () => {
      if (cancelled) return;
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        return;
      }
      if (attempts < 24) {
        attempts += 1;
        requestAnimationFrame(scrollToHash);
      }
    };

    requestAnimationFrame(scrollToHash);
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.hash]);

  if (searchParams.get("embed") === "1") {
    return <LandingEmbedPage />;
  }

  return (
    <main
      data-testid="landing-page"
      className="ds-landing min-h-screen bg-[#050505] text-white relative w-full max-w-[100vw] overflow-x-clip"
    >
      <CurrentHomePage />
      <KakaoFloatingButton />
      <Suspense fallback={null}>
        <ChatbotWidget />
      </Suspense>
    </main>
  );
}
